import * as React from 'react';
import * as moment_timezone from 'moment-timezone';
import { WEEK_VIEW, MONTH_VIEW, DAY_VIEW } from '../calendar/calendar.constants';
import { createMinMaxTime } from '../calendar/calendar.util';
import { Event } from '../calendar/calendar.event.types';
import RaisedButton from 'material-ui/RaisedButton';
import EventbarComponent from './calendar.eventbar.component';
import { AuthState } from '../auth/auth.types';
import { SignupState } from '../admin/signup.types';
import { DashboardState } from './dashboard.types';
import CreateEventModal from './create.event.modal';
import { isCalendarDisabled, cannotWrite, NOT_ENOUGH_PERMISSION_IMAGE } from '../util/permission.util';
import { AdminState } from '../admin/admin.types';
import { CALENDAR } from '../account/Constants';
import { BusinessState } from '../business/business.types';

const BigCalendar = require('react-big-calendar');

type Props = {
  readonly events: Array<Event>
  readonly authState: AuthState
  readonly adminState: AdminState
  readonly businessState: BusinessState
  readonly signupState: SignupState
  readonly dashboardState: DashboardState
  readonly createCalendarEvent: (
    token: string, eventType: string, title: string, startDate: number, endDate: number, note: string
  ) => Promise<Response>
  readonly loadingMembers: boolean
};

type States = {
  readonly start: Date
  readonly events: Array<Event>
  readonly createEventModalOpen: boolean
  readonly editEventModalOpen: boolean
  readonly selectedEvent: Event
  readonly slotInfo?: any
};

class CalendarComponent extends React.Component<Props, States> {

  constructor(props: Props) {
    super(props);

    this.state = {
      events: [],
      start: new Date(),
      createEventModalOpen: false,
      editEventModalOpen: false,
      selectedEvent: {id: 0, title: ''}
    };

    BigCalendar.momentLocalizer(moment_timezone);

    this.handleSelectEvent = this.handleSelectEvent.bind(this);
    this.handleCreateEvent = this.handleCreateEvent.bind(this);
    this.pickColorForEvent = this.pickColorForEvent.bind(this);
  }

  componentWillReceiveProps(nextProps: Props) {
    if (this.props !== nextProps) {
      this.setState({
        events: nextProps.events
      });
    }
  }

  componentWillUnmount() {
    this.setState({
      createEventModalOpen: false,
      editEventModalOpen: false
    });
  }

  render() {
    const { events, createEventModalOpen, editEventModalOpen, selectedEvent, slotInfo } = this.state;
    const { authState, adminState } = this.props;

    return(
      <div>
        <div className="row vh-75 justify-content-center">
          <div className="col-3 text-center">
            <RaisedButton 
              label="Create"
              primary={true}
              onClick={this.handleCreateEvent}
              disabled={cannotWrite(CALENDAR, authState, adminState)}
            />
            <div className="pt-4">Today's Events</div>
            <div
              className="pt-2"
              style={{
                color: 'white',
                overflowY: 'auto',
                width: '100%',
                maxHeight: 350
              }}
            >
              {this.getTodaysEvents(events)}
            </div>
          </div>
          <div className="col-9 bg-white">
            {this.renderCalendar()}
          </div>
        </div>
        <CreateEventModal 
          modalOpen={createEventModalOpen} 
          editMode={false} 
          slotInfo={slotInfo} 
          loadingMembers={this.props.loadingMembers}
        />
        <CreateEventModal 
          modalOpen={editEventModalOpen} 
          editMode={true} 
          selectedEvent={selectedEvent}
          loadingMembers={this.props.loadingMembers}
        />
      </div>
    );
  }

  private renderCalendar = (): JSX.Element => {
    const { events, start } = this.state;
    const { authState, adminState } = this.props;

    if (!isCalendarDisabled(authState, adminState)) {
      return(
        <BigCalendar
          selectable={true}
          onSelectSlot={this.handleCreatEventWith}
          events={events}
          defaultView={MONTH_VIEW}
          views={[MONTH_VIEW, WEEK_VIEW, DAY_VIEW]}
          min={createMinMaxTime(7, start)}
          max={createMinMaxTime(23, start)}
          messages={{allDay: ''}}
          onSelectEvent={this.handleSelectEvent}
          eventPropGetter={this.pickColorForEvent}
          className="pa2"
        />
      );
    } else {
      return(
        <div className="row">
          <div className="col-12 text-center">
            <img src={NOT_ENOUGH_PERMISSION_IMAGE} className="img-responsive"/>
          </div>
        </div>
      );
    }
  }

  private pickColorForEvent = (event: Event, start: Date, end: Date, isSelected: boolean) => {
    return {
      style: {
        backgroundColor: event.backgroundColor
      }
    };
  }

  private getTodaysEvents = (events: Array<Event>): Array<JSX.Element> => {
    const today = new Date();
    return events
      .filter((event: Event) => 
        new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1, 23, 59, 59) < event.start! && 
        event.start! < new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1, 0, 0, 0)
      )
      .map((event: Event, index: number) => {
      return(
        <div key={index} className="pt-2 pr-2">
          <EventbarComponent event={event}/>
        </div>
      );
    });
  }

  private handleCreatEventWith = (slotInfo): void => {
    const { createEventModalOpen } = this.state;

    if (createEventModalOpen) {
      this.setState(
        {
          editEventModalOpen: false, 
          createEventModalOpen: false, 
          selectedEvent: {id: 0, title: ''},
          slotInfo: slotInfo
        },
        () => this.setState({createEventModalOpen: true}));  
    } else {
      this.setState(
        {
          createEventModalOpen: true, 
          editEventModalOpen: false,
          slotInfo: slotInfo
        }
      );
    }
  }

  private handleCreateEvent = (): void => {
    const { createEventModalOpen } = this.state;

    if (createEventModalOpen) {
      this.setState(
        {editEventModalOpen: false, createEventModalOpen: false, selectedEvent: {id: 0, title: ''}},
        () => this.setState({createEventModalOpen: true}));  
    } else {
      this.setState({createEventModalOpen: true, editEventModalOpen: false});
    }
  }

  private handleSelectEvent = (event: Event): void => {
    const { authState, adminState } = this.props;
    const { editEventModalOpen } = this.state;
    
    if (cannotWrite(CALENDAR, authState, adminState)) {
      return;
    } else if (editEventModalOpen) {
      return this.setState(
        {editEventModalOpen: false, createEventModalOpen: false, selectedEvent: {id: 0, title: ''}},
        () => this.setState({editEventModalOpen: true, selectedEvent: event})
      );  
    } else {
      return this.setState({editEventModalOpen: true, createEventModalOpen: false, selectedEvent: event});
    }
  }
}

export default CalendarComponent;



// WEBPACK FOOTER //
// ./src/dashboard/calendar.component.tsx